import React, { useCallback } from 'react';
import {
  Divider,
  Flex,
  IconChevronRight,
  IconEmptyInform,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import { TitleContent } from './components/TitleContent';
import { useGoodsDetail } from '../../queries/useGoodsDetail';
import { isDeliveryTypeIsToday } from '../../utils/isDeliveryTypeIsToday';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { isNil, isNotNil } from 'shared/src/utils';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useDeliveryTypeTodayInfoBottomSheet } from './components/DeliveryTypeTodayInfoBottomSheet';
import { StartShippingInfo } from './components/StartShippingInfo';
import { useConfigs } from 'shared/src/queries';
import { GoodsAwardRow } from './components/GoodsAwardRow';
import { useLogClickTooltip } from './hooks/useLogClickTooltip';
import { useLogClickTodayDelivery } from './hooks/useLogClickTodayDelivery';
import { useDeliveryWarningMessage } from './hooks/useDeliveryWarningMessage';
import { ArrivalExpectedDay } from './components/ArrivalExpectedDay';
import { IconTooltip } from './components/IconTooltip';
import { useDeliveryInfoMessage } from './hooks/useDeliveryInfoMessage';

const DeliveryInfoContent = () => {
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();
  const { show: showDeliveryTypeTodayInfo } =
    useDeliveryTypeTodayInfoBottomSheet();
  const logClickTooltip = useLogClickTooltip();
  const logClickTodayDelivery = useLogClickTodayDelivery();
  const { openDeeplink } = useOpenDeeplink();

  const { data: configs } = useConfigs();

  const onPressDeliveryTypeToday = useCallback(() => {
    if (isNil(goodsBasic)) {
      return;
    }
    logClickTooltip();
    showDeliveryTypeTodayInfo({
      iconUrl: goodsBasic.delivery_type_info.icon_url,
      guideImageUrl: configs?.today_delivery.integrate_guide_image ?? null,
    });
  }, [goodsBasic, showDeliveryTypeTodayInfo, logClickTooltip, configs]);

  const onPressTodayDeliveryMore = useCallback(() => {
    if (isNil(configs)) return;
    logClickTodayDelivery();

    openDeeplink(configs.today_delivery.department_deeplink);
  }, [configs, logClickTodayDelivery, openDeeplink]);

  const deliveryWarningMessage = useDeliveryWarningMessage();
  const deliveryInfoMessage = useDeliveryInfoMessage();

  if (!goodsDetail) return null;

  return (
    <Flex>
      <Divider size={'medium'} />
      <Flex
        padding={{
          vertical: 20,
          horizontal: 16,
          top: isDeliveryTypeIsToday(goodsBasic) ? 14 : 20,
        }}
        gap={12}
      >
        <Flex>
          <GoodsAwardRow />
          <TitleContent
            title={'배송정보'}
            tooltip={
              isDeliveryTypeIsToday(goodsBasic) ||
              goodsBasic?.delivery_type_info.delivery_type === 'reserved'
                ? undefined
                : '제주도 및 도서산간 일부 지역의 경우, 추가 배송비가 발생할 수 있습니다.'
            }
          >
            <Flex gap={4}>
              {isNotNil(goodsBasic) && isDeliveryTypeIsToday(goodsBasic) ? (
                <Flex.Row gap={8} alignItems={'center'}>
                  <Flex.Row
                    gap={2}
                    alignItems={'center'}
                    onPress={onPressDeliveryTypeToday}
                    testID={'오늘출발툴팁'}
                  >
                    {isNotNil(goodsBasic?.delivery_type_info.icon_url) && (
                      <RemoteImage
                        uri={goodsBasic?.delivery_type_info.icon_url}
                        width={49}
                        height={13}
                      />
                    )}
                    <IconTooltip />
                  </Flex.Row>

                  <Flex.Row
                    alignItems={'center'}
                    justifyContent={'center'}
                    padding={{ vertical: 6, left: 8, right: 6 }}
                    onPress={onPressTodayDeliveryMore}
                    borderWidth={1}
                    borderColor={'border_tertiary'}
                    borderRadius={16}
                    testID={'오늘출발전체보기버튼'}
                    gap={4}
                  >
                    <Typography type={'subtitle5'} color={'content_secondary'}>
                      오늘출발 전체보기
                    </Typography>
                    <IconChevronRight size={12} />
                  </Flex.Row>
                </Flex.Row>
              ) : (
                <Typography type={'body2'}>{deliveryInfoMessage}</Typography>
              )}
            </Flex>
          </TitleContent>
          {isNotNil(goodsBasic) && isDeliveryTypeIsToday(goodsBasic) && (
            <TitleContent title={''}>
              <Typography type={'body2'}>{deliveryInfoMessage}</Typography>
            </TitleContent>
          )}
        </Flex>
        <Flex>
          <TitleContent
            title={'출발예정'}
            tooltip={
              isNotNil(goodsBasic?.delivery_type_info.icon_url)
                ? undefined
                : '최근 3개월 배송 데이터를 분석하여 예측한 출발 예정일입니다. 판매사 및 택배사 사정 또는 배송지에 따라 실제 출발일은 다를 수 있습니다.'
            }
          >
            <StartShippingInfo />
          </TitleContent>
          <ArrivalExpectedDay />
        </Flex>
        <Flex.Row gap={4} alignItems={'flex-start'} margin={{ top: 4 }}>
          <Flex padding={{ top: 2 }}>
            <IconEmptyInform size={12} />
          </Flex>
          <Typography type={'body3'} color={'content_tertiary'}>
            {deliveryWarningMessage}
          </Typography>
        </Flex.Row>
      </Flex>
      <Divider size={'medium'} />
    </Flex>
  );
};

export const DeliveryInfo = () => {
  return (
    <React.Suspense fallback={<></>}>
      <DeliveryInfoContent />
    </React.Suspense>
  );
};
